import type { Category, PlanningCycle, SpaceTemplateBundle, TaskTemplate, Todo } from "../../domain/entities.ts";
import type { Attachment, Root } from "../../domain/app-types.ts";
import type { StoredEntity } from "../../infrastructure/storage/indexed-db.ts";
import type { PortableAttachment, PortableSpace } from "./transfer.ts";
import { parseReferenceHistory, type ReferenceRevision } from "../notes/reference-history.ts";
import type { ProgressReporter } from "../../ui/operation-progress.ts";
import { parseContentHistory, type ContentRevision } from "../notes/content-history.ts";

export function portableSpaceProjection(root: Root, entities: StoredEntity[], attachments: PortableAttachment[]): PortableSpace {
  const schema = Object.fromEntries((root.task_defaults?.custom_fields || []).map(field => [field.id, field]));
  const snapshot = (stored: Category | Todo, ids: string[]) => Object.fromEntries(ids.flatMap(id => { const field = schema[id] || stored.customFieldSchema?.[id]; return field ? [[id, structuredClone(field)]] : []; }));
  return {
    id: root.id,
    name: root.name,
    color: root.color,
    categories: entities.filter(entity => entity.kind === "category").map(entity => { const value = entity.projection as Category; return { ...structuredClone(value), customFieldSchema: snapshot(value, value.customFieldIds || []) }; }),
    tasks: entities.filter(entity => entity.kind === "todo").map(entity => { const value = entity.projection as Todo; return { ...structuredClone(value), customFieldSchema: snapshot(value, Object.keys(value.customFields || {})) }; }),
    attachments,
    templates: entities.filter(entity => entity.kind === "template").map(entity => structuredClone(entity.projection as TaskTemplate)),
    spaceTemplates: entities.filter(entity => entity.kind === "space_template").map(entity => structuredClone(entity.projection as SpaceTemplateBundle)),
    epics: entities.filter(entity => entity.kind === "epic").map(entity => structuredClone(entity.projection as import("../../domain/entities.ts").Epic)),
    cycles: entities.filter(entity => entity.kind === "cycle").map(entity => structuredClone(entity.projection as PlanningCycle)),
  };
}

function blobBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export async function exportPortableSpace(root: Root, entities: StoredEntity[], request: (path: string, init?: RequestInit) => Promise<Response | null>, progress?: ProgressReporter): Promise<PortableSpace> {
  const tasks = entities.filter(entity => entity.kind === "todo").map(entity => entity.projection as Todo);
  const attachments: PortableAttachment[] = [];
  let checked = 0;
  progress?.({ stage: "Checking task files", completed: 0, total: tasks.length });
  for (const task of tasks) {
    const response = await request(`/api/v1/tasks/${task.id}/attachments`, { credentials: "include" });
    if (!response?.ok) throw new Error("Connect to the server to include attachments in this export.");
    for (const item of await response.json() as Attachment[]) {
      const download = await request(`/api/v1/tasks/${task.id}/attachments/${item.id}`, { credentials: "include" });
      if (!download?.ok) throw new Error(`Could not include attachment “${item.file_name}”.`);
      attachments.push({ taskId: task.id, fileName: item.file_name, contentType: item.content_type, data: await blobBase64(await download.blob()) });
    }
    progress?.({ stage: "Checking task files", completed: ++checked, total: tasks.length });
  }
  const space = portableSpaceProjection(root, entities, attachments);
  const contentHistory: Record<string, ContentRevision[]> = {};
  for (const task of tasks.filter(task => !task.deleted)) {
    const response = await request(`/api/v1/entities/${task.id}/content-history`, { credentials: "include" });
    if (!response?.ok) throw new Error("Connect with current task access to include content versions in this export.");
    const revisions = parseContentHistory(await response.json(), "todo");
    if (revisions.length) contentHistory[task.id] = revisions;
  }
  const history: Record<string, ReferenceRevision[]> = {};
  const categories = space.categories.filter(item => !item.deleted && item.referenceNote !== undefined);
  checked = 0;
  progress?.({ stage: "Reading Reference history", completed: 0, total: categories.length });
  for (const category of categories) {
    const response = await request(`/api/v1/collections/${category.id}/reference-history`, { credentials: "include" });
    if (!response?.ok) throw new Error("Connect with current collection access to include Reference history in this export.");
    const revisions = parseReferenceHistory(await response.json());
    if (revisions.length) history[category.id] = revisions;
    progress?.({ stage: "Reading Reference history", completed: ++checked, total: categories.length });
  }
  return { ...space, ...(Object.keys(history).length ? { referenceHistory: history } : {}), ...(Object.keys(contentHistory).length ? { contentHistory } : {}) };
}
